import React, { useState, useEffect } from 'react'
import { useApi } from '../../contexts/ApiContext'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { FileText, Calendar, DollarSign, AlertCircle, CheckCircle, Clock, Plus } from 'lucide-react'
import ChatButton from '../../components/ChatButton'
import { trackPageView, trackClaimView, openChatWithContext } from '../../utils/eventTracking'

const CustomerClaims = () => {
  const { getClaims, getUserPoliciesWithClaims } = useApi()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [claims, setClaims] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Get user_id from URL parameters
  const userId = searchParams.get('user_id')

  // Track page view on mount
  useEffect(() => {
    trackPageView('Customer Claims', { user_id: userId })
  }, [userId])

  useEffect(() => {
    if (userId) {
      loadClaims()
    } else {
      setError('No user ID provided')
      setLoading(false)
    }
  }, [userId])

  const loadClaims = async () => {
    try {
      setLoading(true)
      console.log('Loading claims for user ID from URL:', userId)
      
      // Use the corrected SQL function that gets policies with claims
      const policiesWithClaimsData = await getUserPoliciesWithClaims(userId)
      console.log('User policies with claims data:', policiesWithClaimsData)
      
      // Filter out only the rows that have claim data (where claim_id is not null)
      const claimsOnly = policiesWithClaimsData.filter(item => item.claim_id !== null)
      console.log('Filtered claims only:', claimsOnly)
      
      setClaims(claimsOnly || [])
    } catch (err) {
      console.error('Error loading claims:', err)
      setError('Failed to load claims')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'bg-emerald-200 text-emerald-900 border-2 border-emerald-400 shadow-sm'
      case 'APPROVED': return 'bg-green-200 text-green-900 border border-green-300'
      case 'DRAFT': return 'bg-gray-200 text-gray-800 border border-gray-300'
      case 'SUBMITTED': return 'bg-blue-200 text-blue-900 border border-blue-300'
      case 'UNDER_REVIEW': return 'bg-yellow-200 text-yellow-900 border border-yellow-300'
      case 'REJECTED': return 'bg-red-200 text-red-900 border border-red-300'
      case 'CLOSED': return 'bg-gray-200 text-gray-800 border border-gray-300'
      case 'REOPENED': return 'bg-orange-200 text-orange-900 border border-orange-300'
      default: return 'bg-gray-200 text-gray-800 border border-gray-300'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return <CheckCircle className="h-4 w-4" />
      case 'APPROVED': return <CheckCircle className="h-4 w-4" />
      case 'DRAFT': return <FileText className="h-4 w-4" />
      case 'SUBMITTED': return <Clock className="h-4 w-4" />
      case 'UNDER_REVIEW': return <Clock className="h-4 w-4" />
      case 'REJECTED': return <AlertCircle className="h-4 w-4" />
      case 'CLOSED': return <FileText className="h-4 w-4" />
      case 'REOPENED': return <Clock className="h-4 w-4" />
      default: return <Clock className="h-4 w-4" />
    }
  }

  const getApprovalStatusColor = (approvalStatus) => {
    switch (approvalStatus) {
      case 'PENDING': return 'bg-yellow-200 text-yellow-900 border border-yellow-300'
      case 'APPROVED': return 'bg-green-200 text-green-900 border border-green-300'
      case 'REJECTED': return 'bg-red-200 text-red-900 border border-red-300'
      case 'PARTIALLY_APPROVED': return 'bg-orange-200 text-orange-900 border border-orange-300'
      default: return 'bg-gray-200 text-gray-800 border border-gray-300'
    }
  }

  const handleClaimClick = (claimId) => {
    // Navigate to claim details page with claim ID in URL
    navigate(`/customer/claims/${claimId}?user_id=${userId}`)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Claims</h1>
          <p className="text-gray-600">View and manage your insurance claims</p>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading claims...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Claims</h1>
          <p className="text-gray-600">View and manage your insurance claims</p>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Claims</h3>
          <p className="text-red-500">{error}</p>
          {!userId && (
            <p className="text-sm text-gray-500 mt-2">
              Please ensure you're accessing this page with a valid user ID parameter.
            </p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">My Claims</h1>
          <p className="text-xs sm:text-sm text-gray-600">View and manage your insurance claims ({claims.length} claims found)</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <ChatButton type="claims" size="small" variant="secondary" />
          <button 
            onClick={() => {
              openChatWithContext(
                'I would like to file a new claim. Can you help me with the claim submission process and required documents?',
                'claims_page_new_claim'
              )
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>New Claim</span>
          </button>
        </div>
      </div>

      {claims.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {claims.map((claim, index) => (
            <div 
              key={claim.claim_id || `claim-${index}`} 
              className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-200 cursor-pointer transform hover:scale-105"
              onClick={() => handleClaimClick(claim.claim_id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-lg mb-2">{claim.claim_number || 'N/A'}</h3>
                  <p className="text-sm text-gray-600 font-medium">{claim.claim_category || 'N/A'} • {claim.claim_stage || 'N/A'}</p>
                </div>
                <span className={`px-3 py-1 text-xs font-bold rounded-full flex items-center space-x-1 ${getStatusColor(claim.claim_status)}`}>
                  {getStatusIcon(claim.claim_status)}
                  <span>{claim.claim_status}</span>
                </span>
              </div>
              
              <div className="space-y-4">
                {/* Financial Information */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Total Bill Amount:</span>
                    <span className="text-lg font-bold text-gray-900">₹{claim.total_bill_amount?.toLocaleString() || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Approved Amount:</span>
                    <span className="text-lg font-bold text-green-600">₹{claim.approved_amount?.toLocaleString() || '0'}</span>
                  </div>
                </div>

                {/* Claim Information */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Type:</span>
                    <span className="text-sm font-medium text-gray-700">{claim.claim_type || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Mode:</span>
                    <span className="text-sm font-medium text-gray-700">{claim.claim_mode || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Status:</span>
                    <span className={`px-2 py-1 text-xs font-bold rounded-full ${getStatusColor(claim.claim_status)}`}>
                      {claim.claim_status || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Claims Found</h3>
          <p className="text-gray-500">You haven't submitted any claims yet.</p>
        </div>
      )}
    </div>
  )
}

export default CustomerClaims